import { addOrUpdateGeoJsonSource, removeSourceAndLayers } from '../utils/maplibre-helpers';
import { NOISE_INTERACTIVE_LAYER_IDS } from '../core/noise-layer-runtime';
import { pointInPolygonGeometry } from '../utils/coords';
import { nearestPointHits } from '../utils/point-hit-area';
import EducationFacilitiesLayer from '../layers/EducationFacilitiesLayer';
import { educationLayerRuntime } from '../core/education-layer-runtime';

export const CLICK_MARKER_SOURCE_ID = 'src-interactive-hit-click-marker';
export const CLICK_MARKER_LAYER_IDS = ['lyr-interactive-hit-click-ring', 'lyr-interactive-hit-click-dot'];

function getNoiseLayerKey(layerId) {
    if (String(layerId || '').indexOf('noiseLdwn') !== -1) return 'noiseLdwn';
    if (String(layerId || '').indexOf('noiseLn') !== -1) return 'noiseLn';
    if (String(layerId || '').indexOf('noiseMonitoring') !== -1) return 'noiseMonitoring';
    return null;
}

export function normalizeNoiseHit(feature) {
    var props = feature && feature.properties ? feature.properties : null;
    var layerKey = getNoiseLayerKey(feature && feature.layer ? feature.layer.id : null);
    if (!props || !layerKey) {
        return null;
    }

    var sourceId = layerKey === 'noiseMonitoring'
        ? (props.id != null ? props.id
            : (props.facilityId != null ? props.facilityId
                : [props.indicator || '', props.pointCount || '', props.value || ''].join(':')))
        : (props.noiseType || props.id || props.indicator || 'contour');

    return {
        type: 'noise',
        id: layerKey + ':' + String(sourceId),
        layerKey: layerKey,
        properties: props,
        feature: feature,
    };
}

export function normalizeEducationHit(feature) {
    var props = feature && feature.properties ? feature.properties : null;
    if (!props || props.id == null) {
        return null;
    }

    return {
        type: 'education',
        id: String(props.id),
        properties: props,
        feature: feature,
    };
}

export function noiseGeometryHitsAtLngLat(getFeatures, latlng) {
    if (typeof getFeatures !== 'function' || !latlng) return [];
    var point = [Number(latlng.lng), Number(latlng.lat)];
    if (!isFinite(point[0]) || !isFinite(point[1])) return [];

    var hits = [];
    ['noiseLdwn', 'noiseLn'].forEach(function(layerKey) {
        var layerId = 'lyr-' + layerKey + '-fill';
        (getFeatures(layerKey) || []).forEach(function(feature) {
            if (!pointInPolygonGeometry(point, feature && feature.geometry)) return;
            var normalized = normalizeNoiseHit(Object.assign({}, feature, {
                layer: { id: layerId }
            }));
            if (normalized) hits.push(normalized);
        });
    });
    return hits;
}

export function createInteractiveHitDispatcher(config) {

    function clearClickMarker() {
        var map = config.getMap();
        if (!map) {
            return;
        }

        removeSourceAndLayers(map, CLICK_MARKER_SOURCE_ID, CLICK_MARKER_LAYER_IDS);
    }

    function showClickMarker(latlng) {
        var map = config.getMap();
        if (!map || !latlng) {
            return;
        }

        addOrUpdateGeoJsonSource(map, CLICK_MARKER_SOURCE_ID, {
            type: 'FeatureCollection',
            features: [{
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [latlng.lng, latlng.lat]
                }
            }]
        });

        if (!map.getLayer(CLICK_MARKER_LAYER_IDS[0])) {
            map.addLayer({
                id: CLICK_MARKER_LAYER_IDS[0],
                type: 'circle',
                source: CLICK_MARKER_SOURCE_ID,
                paint: {
                    'circle-radius': 11,
                    'circle-color': 'rgba(255,255,255,0.18)',
                    'circle-stroke-color': '#f97316',
                    'circle-stroke-width': 3,
                    'circle-opacity': 1
                }
            });
        }

        if (!map.getLayer(CLICK_MARKER_LAYER_IDS[1])) {
            map.addLayer({
                id: CLICK_MARKER_LAYER_IDS[1],
                type: 'circle',
                source: CLICK_MARKER_SOURCE_ID,
                paint: {
                    'circle-radius': 4,
                    'circle-color': '#f97316',
                    'circle-stroke-color': 'rgba(255,255,255,0.96)',
                    'circle-stroke-width': 2,
                    'circle-opacity': 1
                }
            });
        }
    }

    function getAvailableLayerIds(layerIds) {
        var map = config.getMap();
        if (!map || !Array.isArray(layerIds)) {
            return [];
        }

        return layerIds.filter(function(layerId) {
            return !!map.getLayer(layerId);
        });
    }

    function queryRenderedFeatures(point, layerIds) {
        var map = config.getMap();
        var availableLayerIds = getAvailableLayerIds(layerIds);
        if (!map || !point || !availableLayerIds.length) {
            return [];
        }

        try {
            return map.queryRenderedFeatures(point, { layers: availableLayerIds });
        } catch (_error) {
            return [];
        }
    }

    function forEachCoordinate(geometry, iterator) {
        if (!geometry || !geometry.coordinates) {
            return;
        }

        function visit(node) {
            if (!Array.isArray(node)) {
                return;
            }

            if (node.length >= 2 && typeof node[0] === 'number' && typeof node[1] === 'number') {
                iterator(node);
                return;
            }

            node.forEach(visit);
        }

        visit(geometry.coordinates);
    }

    function getFeatureLngLatBounds(feature) {
        var minLng = Infinity;
        var minLat = Infinity;
        var maxLng = -Infinity;
        var maxLat = -Infinity;

        forEachCoordinate(feature && feature.geometry, function(coord) {
            if (coord[0] < minLng) minLng = coord[0];
            if (coord[0] > maxLng) maxLng = coord[0];
            if (coord[1] < minLat) minLat = coord[1];
            if (coord[1] > maxLat) maxLat = coord[1];
        });

        if (!isFinite(minLng) || !isFinite(minLat) || !isFinite(maxLng) || !isFinite(maxLat)) {
            return null;
        }

        return {
            minLng: minLng,
            minLat: minLat,
            maxLng: maxLng,
            maxLat: maxLat,
        };
    }


    function getOtoczenieId(feature) {
        var props = feature && feature.properties ? feature.properties : {};
        if (feature && feature.id != null) {
            return String(feature.id);
        }
        if (props.id != null) {
            return String(props.id);
        }
        if (props.id_bdot10k != null) {
            return String(props.id_bdot10k);
        }
        if (props.objectid != null) {
            return String(props.objectid);
        }

        var bounds = getFeatureLngLatBounds(feature);
        if (!bounds) {
            return null;
        }

        return [
            'otoczenie',
            props.layer_code || '',
            props.category_label || '',
            bounds.minLng.toFixed(6),
            bounds.minLat.toFixed(6),
            bounds.maxLng.toFixed(6),
            bounds.maxLat.toFixed(6)
        ].join(':');
    }

    function normalizeTransactionHit(feature) {
        var props = feature && feature.properties ? feature.properties : null;
        if (!props || !props.tran_lokalny_id_iip) {
            return null;
        }

        return {
            type: 'transaction',
            id: String(props.tran_lokalny_id_iip),
            properties: props,
            feature: feature,
        };
    }

    function normalizeGunbHit(feature) {
        var props = feature && feature.properties ? feature.properties : null;
        if (!props || props.id == null) {
            return null;
        }

        return {
            type: 'gunb',
            id: String(props.id),
            properties: props,
            feature: feature,
        };
    }

    function normalizeBuildingHit(feature) {
        var props = feature && feature.properties ? feature.properties : null;
        if (!props || props.id_budynku == null) {
            return null;
        }

        return {
            type: 'building',
            id: String(props.id_budynku),
            properties: props,
            feature: feature,
        };
    }

    function normalizeSignalHit(feature) {
        var props = feature && feature.properties ? feature.properties : null;
        if (!props || props.id == null || props.entity_id == null || !props.target_type) {
            return null;
        }

        return {
            type: 'signal',
            id: String(props.id),
            properties: props,
            feature: feature,
        };
    }

    function normalizeParcelHit(feature, props) {
        var resolvedProps = props || (feature && feature.properties ? feature.properties : null);
        if (!resolvedProps || resolvedProps.id_dzialki == null) {
            return null;
        }

        return {
            type: 'parcel',
            id: String(resolvedProps.id_dzialki),
            properties: resolvedProps,
            feature: feature || null,
        };
    }

    function normalizeOtoczenieHit(feature) {
        var props = feature && feature.properties ? feature.properties : null;
        if (!props) {
            return null;
        }

        var id = getOtoczenieId(feature);
        if (!id) {
            return null;
        }

        return {
            type: 'otoczenie',
            id: id,
            properties: props,
            feature: feature,
        };
    }

    function mergeHits(targetHits, incomingHits) {
        var seen = {};

        targetHits.forEach(function(hit) {
            if (hit) {
                seen[hit.type + ':' + hit.id] = true;
            }
        });

        incomingHits.forEach(function(hit) {
            if (!hit) {
                return;
            }

            var key = hit.type + ':' + hit.id;
            if (seen[key]) {
                return;
            }

            seen[key] = true;
            targetHits.push(hit);
        });

        return targetHits;
    }

    function collectRenderedHits(point, latlng) {
        var zoom = config.mapZoom();
        var hits = [];

        mergeHits(hits, queryRenderedFeatures(point, NOISE_INTERACTIVE_LAYER_IDS).map(normalizeNoiseHit));
        mergeHits(hits, noiseGeometryHitsAtLngLat(config.getNoiseFeatures, latlng));

        if (config.isTransakcjeActive() && zoom >= config.INDIVIDUAL_ZOOM_THRESHOLD) {
            // Enlarged hit circles overlap in a dense block: the nearest dot wins.
            var map = config.getMap();
            var transactionFeatures = nearestPointHits(
                queryRenderedFeatures(point, config.TransactionsLayer.BASE_LAYER_IDS),
                point,
                function(coordinates) { return map.project(coordinates); },
                config.TransactionsLayer.VISIBLE_DOT_RADIUS
            );
            mergeHits(hits, transactionFeatures.map(normalizeTransactionHit));
        }

        if (educationLayerRuntime.isQueryable()) {
            // Enlarged hit circles: the nearest point wins, with the facilities sharing its address.
            var educationMap = config.getMap();
            mergeHits(hits, nearestPointHits(
                queryRenderedFeatures(point, [EducationFacilitiesLayer.HIT_LAYER_ID]),
                point,
                function(coordinates) { return educationMap.project(coordinates); },
                EducationFacilitiesLayer.VISIBLE_DOT_RADIUS
            ).map(normalizeEducationHit));
        }

        if (config.isGunbActive() && zoom >= config.GunbLayer.INDIVIDUAL_ZOOM_THRESHOLD) {
            mergeHits(hits, queryRenderedFeatures(point, config.GunbLayer.BASE_LAYER_IDS).map(normalizeGunbHit));
        }


        if (config.isOtoczenieActive() && zoom >= config.OtoczenieLayer.MIN_ZOOM) {
            mergeHits(hits, queryRenderedFeatures(point, config.getOtoczenieLayerIds()).map(normalizeOtoczenieHit));
        }

        if (config.isBudynkiActive() && zoom >= config.BudynkiLayer.MIN_ZOOM) {
            mergeHits(hits, queryRenderedFeatures(point, config.BudynkiLayer.BASE_LAYER_IDS).map(normalizeBuildingHit));
        }

        if (config.isDzialkiActive() && zoom >= config.DzialkiLayer.MIN_ZOOM) {
            mergeHits(hits, queryRenderedFeatures(point, config.getParcelLayerIds()).map(function(feature) {
                return normalizeParcelHit(feature);
            }));
        }

        return hits;
    }



    function getHitAnchor(hit, fallbackLatlng) {
        if (hit && hit.feature && typeof config.getFeatureCentroid === 'function') {
            return config.getFeatureCentroid(hit.feature) || fallbackLatlng;
        }

        return fallbackLatlng;
    }


    function openSingleHit(latlng, hit) {
        if (!hit) {
            return false;
        }

        var anchor = getHitAnchor(hit, latlng);

        if (hit.type === 'transaction') {
            config.selectTransactionByFeature(hit.properties, anchor);
            return true;
        }

        if (hit.type === 'gunb') {
            config.selectGunbPermit(hit.feature, anchor);
            return true;
        }

        if (hit.type === 'otoczenie') {
            config.selectOtoczenieFeature(hit.feature, anchor);
            return true;
        }

        if (hit.type === 'building') {
            config.selectBuilding(hit.properties, anchor);
            return true;
        }

        if (hit.type === 'signal') {
            config.selectSignalHit(hit.properties, anchor);
            return true;
        }

        if (hit.type === 'noise') {
            config.selectNoiseHit(hit, anchor);
            return true;
        }

        if (hit.type === 'education') {
            config.selectEducationHit(hit, anchor);
            return true;
        }

        config.selectParcel(hit.properties, anchor);
        return true;
    }


    function dispatchMapClick(latlng, point) {
        return config.dispatchPointQuery(latlng, point);
    }

    return {
        collectRenderedHits: collectRenderedHits,
        openSingleHit: openSingleHit,
        clearClickMarker: clearClickMarker,
        dispatchMapClick: dispatchMapClick,
        showClickMarker: showClickMarker,
    };
}
